/**
 * BestOddsService - Service for determining the best odds across sportsbooks
 * Uses industry standard approach: highest odds value = best odds for all markets
 */
class BestOddsService {
  
  /**
   * Determines if a bookmaker has the best odds for a specific outcome
   * @param {Object} game - The game object with all bookmaker data
   * @param {string} bookmakerTitle - The bookmaker to check
   * @param {string} teamName - Team name for team-based markets, or 'Over'/'Under' for totals
   * @param {string} marketType - Market type (h2h, spreads, totals, etc.)
   * @param {string[]} displayedBookmakers - Array of bookmaker titles that are actually displayed
   * @returns {boolean} - True if this bookmaker has the best odds among displayed bookmakers
   */
  isBestOdds(game, bookmakerTitle, teamName, marketType, displayedBookmakers = null) {
    const bestOdds = this.getBestOdds(game, teamName, marketType, displayedBookmakers);
    return bestOdds && bestOdds.includes(bookmakerTitle);
  }

  /**
   * Gets all bookmakers that tie for best odds for a specific outcome
   * @param {Object} game - The game object
   * @param {string} teamName - Team name or 'Over'/'Under'  
   * @param {string} marketType - Market type
   * @param {string[]} displayedBookmakers - Array of bookmaker titles that are actually displayed
   * @returns {string[]} - Array of bookmaker titles with best odds among displayed bookmakers
   */
  getBestOdds(game, teamName, marketType, displayedBookmakers = null) {
    const bookmakerOdds = [];
    
    // Validate inputs
    if (!game || !game.bookmakers || !teamName || !marketType) {
      console.warn('BestOddsService: Invalid input data', { game: !!game, teamName, marketType });
      return [];
    }
    
    game.bookmakers?.forEach(bookmaker => {
      // Skip bookmakers that aren't being displayed
      if (displayedBookmakers && !displayedBookmakers.includes(bookmaker.title)) {
        return;
      }
      
      const market = bookmaker.markets?.find(m => m.key === marketType);
      if (!market) return;

      let outcome;
      if (marketType.includes('totals')) {
        // For totals markets, teamName is already 'Over' or 'Under'
        outcome = market.outcomes?.find(o => o.name === teamName);
      } else {
        // For moneyline and spreads, use team name directly
        outcome = market.outcomes?.find(o => o.name === teamName);
      }

      if (outcome && typeof outcome.price === 'number') {
        bookmakerOdds.push({
          bookmaker: bookmaker.title,
          price: outcome.price
        });
      }
    });

    if (bookmakerOdds.length === 0) {
      console.log(`No odds found for ${teamName} in ${marketType}`);
      return [];
    }

    // Find the highest odds value (industry standard for "best odds")
    const bestPrice = Math.max(...bookmakerOdds.map(b => b.price));
    
    // Return all bookmakers that tie for best price
    const bestBookmakers = bookmakerOdds
      .filter(b => b.price === bestPrice)
      .map(b => b.bookmaker);
      
    console.log(`Best odds for ${teamName} in ${marketType}:`, bestBookmakers, `Price: ${bestPrice}`);
    
    return bestBookmakers;
  }

  /**
   * Helper method specifically for totals markets to get best odds for Over/Under
   * @param {Object} game - The game object
   * @param {string} outcomeName - 'Over' or 'Under'
   * @param {string} marketType - totals market type
   * @param {string[]} displayedBookmakers - Array of bookmaker titles that are actually displayed
   * @returns {string[]} - Array of bookmaker titles with best odds
   */
  getBestTotalsOdds(game, outcomeName, marketType, displayedBookmakers = null) {
    return this.getBestOdds(game, outcomeName, marketType, displayedBookmakers);
  }

  /**
   * Helper method to check if bookmaker has best odds for totals outcome
   * @param {Object} game - The game object
   * @param {string} bookmakerTitle - The bookmaker to check
   * @param {string} outcomeName - 'Over' or 'Under'
   * @param {string} marketType - totals market type
   * @param {string[]} displayedBookmakers - Array of bookmaker titles that are actually displayed
   * @returns {boolean} - True if this bookmaker has best odds
   */
  isBestTotalsOdds(game, bookmakerTitle, outcomeName, marketType, displayedBookmakers = null) {
    return this.isBestOdds(game, bookmakerTitle, outcomeName, marketType, displayedBookmakers);
  }
}

export default new BestOddsService();